"use client";

import { useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";

export function useAuthVendorWidgetLoad({
  accessToken,
  companyId,
  policyId,
  widgetContainerSelector,
  extraParameters,
  onSuccess,
  onError,
  isWidgetScriptReady,
  onWidgetReactivated,
  widgetRefreshIntervalMs,
  ...props
}: AuthVendorWidgetLoadProps) {
  const widgetCreatedAt = useRef<number | null>(null);
  const isRefreshing = useRef(false);
  const retryCount = useRef(0);
  const retryLoadWidget = useRef<(() => void) | null>(null);
  const handleWidgetError = useEffectEvent((event: unknown) => {
    const isStartRequestFailure =
      window?.bol?.authvendorwidget?.isStartRequestError?.(event) ?? false;
    const isKnownFatalFailure =
      window?.bol?.authvendorwidget?.isKnownFatalError?.(event) ?? false;

    if (
      isStartRequestFailure &&
      isKnownFatalFailure &&
      onWidgetReactivated &&
      !isRefreshing.current
    ) {
      // A known-fatal /start error means retrying with the same token would
      // just fail the same way again. Force a fresh token via the consumer's
      // reactivation callback instead; once it resolves with a new
      // accessToken, the effect below rebuilds the widget from scratch. This
      // bypasses handleWidgetReactivation's staleness gate since this is an
      // explicit failure, not a background refresh.
      isRefreshing.current = true;
      void onWidgetReactivated().finally(() => {
        isRefreshing.current = false;
      });
      return;
    }

    if (
      isStartRequestFailure &&
      !isKnownFatalFailure &&
      retryCount.current === 0 &&
      retryLoadWidget.current
    ) {
      retryCount.current += 1;
      retryLoadWidget.current();
      return;
    }

    onError(event);
  });
  const loadWidget = useEffectEvent(() => {
    void window?.bol?.authvendorwidget?.loadWidget?.({
      accessToken,
      companyId,
      policyId,
      widgetContainerSelector,
      extraParameters,
      onSuccess,
      onError: handleWidgetError,
      isWidgetScriptReady,
      ...props,
    });
  });

  const destroyWidget = useEffectEvent(() => {
    void window?.bol?.authvendorwidget?.destroyWidget?.();
  });

  const handleWidgetReactivation = useEffectEvent(async () => {
    if (
      onWidgetReactivated === undefined ||
      widgetRefreshIntervalMs === undefined ||
      widgetCreatedAt.current === null ||
      isRefreshing.current ||
      Date.now() - widgetCreatedAt.current < widgetRefreshIntervalMs
    ) {
      return;
    }

    isRefreshing.current = true;
    try {
      await onWidgetReactivated();
    } finally {
      isRefreshing.current = false;
    }
  });

  useLayoutEffect(() => {
    if (isWidgetScriptReady) {
      retryLoadWidget.current = loadWidget;
      retryCount.current = 0;
      loadWidget();
      widgetCreatedAt.current = Date.now();
    }

    return () => {
      retryLoadWidget.current = null;
      destroyWidget();
      widgetCreatedAt.current = null;
    };
  }, [isWidgetScriptReady, accessToken]);

  useEffect(() => {
    const handleReactivation = () => {
      void handleWidgetReactivation();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleReactivation();
      }
    };

    // Safari freezes timers in the bfcache, and restoring from it may skip focus.
    window.addEventListener("pageshow", handleReactivation);
    window.addEventListener("focus", handleReactivation);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handleReactivation);
      window.removeEventListener("focus", handleReactivation);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
}
